import { useEffect, useRef } from 'react';

export interface KeyBinding {
  /** KeyboardEvent.code, e.g. 'KeyF', 'Space', 'Period', 'Digit1'. Physical keys, so ⌥ on a Mac doesn't change them. */
  code: string;
  alt?: boolean;
  shift?: boolean;
  mod?: boolean; // ⌘ on Mac, Ctrl elsewhere
  run: (e: KeyboardEvent) => void;
}

/**
 * Global shortcuts that also work while typing in the flow (they all use ⌥/⌘, so they never collide with text).
 * Avoids Mac dead keys (⌥E, ⌥U, ⌥I, ⌥N, ⌥`) by convention in the bindings.
 */
export const useKeymap = (bindings: KeyBinding[], enabled = true) => {
  const ref = useRef(bindings);
  ref.current = bindings;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      for (const b of ref.current) {
        if (b.code === e.code && !!b.alt === e.altKey && !!b.shift === e.shiftKey && !!b.mod === mod) {
          e.preventDefault();
          e.stopPropagation();
          b.run(e);
          return;
        }
      }
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [enabled]);
};
